import { Navigate, useLocation, useParams } from "react-router-dom";
import { useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import {
  DEFAULT_LANG,
  LANGS,
  STATIC_SLUGS,
  brandSlug,
  catalogPath,
  localizedPath,
  productPath,
  slugToType,
  type Lang,
  type ProductType,
} from "@/lib/url-slugs";

// Reserved roots that pre-existed and never were real product slugs.
const RESERVED_LEGACY_ROOTS = new Set([
  "products", "product", "cart", "auth", "account", "admin", "order", "contact",
  "faq", "shipping-returns", "shipping", "jaunumi", "abonements", "davanu-karte",
  "gift-card", "terms", "privacy", "padel-bumbas", "sale", "subscription",
  "brand-rackets", "nox-raketes", "starvie-raketes", "siux-raketes",
  "head-raketes", "adidas-raketes", "bullpadel-raketes", "wilson-raketes",
  "sitemap.xml", "robots.txt", "lovable",
]);

const LEGACY_STATIC_MAP: Record<string, keyof (typeof STATIC_SLUGS)["lv"]> = {
  cart: "cart",
  auth: "auth",
  account: "account",
  contact: "contact",
  faq: "faq",
  "shipping-returns": "shipping",
  shipping: "shipping",
  terms: "terms",
  privacy: "privacy",
  sale: "sale",
  "gift-card": "giftCard",
  "davanu-karte": "giftCard",
  abonements: "subscription",
  subscription: "subscription",
};

const BRAND_LEGACY_PATHS: Record<string, string> = {
  "nox-raketes": "nox",
  "starvie-raketes": "starvie",
  "siux-raketes": "siux",
  "head-raketes": "head",
  "adidas-raketes": "adidas",
  "bullpadel-raketes": "bullpadel",
  "wilson-raketes": "wilson",
};

function pickLang(): Lang {
  try {
    const stored = localStorage.getItem("padel-corner-lang");
    if (stored === "lv" || stored === "en") return stored;
  } catch {}
  return DEFAULT_LANG;
}

/**
 * Inspects an un-prefixed legacy URL and redirects it to the new
 * language-prefixed clean URL. Mounted as the wildcard route below
 * `/admin/*` and `/:lang/*`.
 */
export default function LegacyRedirect() {
  const { pathname, search } = useLocation();
  const lang = pickLang();
  const params = new URLSearchParams(search);
  const segments = pathname.split("/").filter(Boolean);

  // Root → home
  if (segments.length === 0) {
    return <Navigate to={`/${lang}`} replace />;
  }

  const first = segments[0].toLowerCase();

  // /products[?type=...&brand=...&...]
  if (first === "products") {
    // /products/:slug = legacy product detail
    if (segments[1]) {
      return <Navigate to={productPath(lang, segments[1])} replace />;
    }
    const type = params.get("type");
    const brand = params.get("brand");
    const t = type ? (type.toLowerCase() as ProductType) : null;
    if (t && ["rackets", "shoes", "apparel", "bags", "balls", "accessories"].includes(t)) {
      params.delete("type");
      params.delete("brand");
      const qs = params.toString();
      const to = catalogPath(lang, t, brand) + (qs ? `?${qs}` : "");
      return <Navigate to={to} replace />;
    }
    // Catch-all: keep search / preserve other query params; route to localized search.
    const qs = params.toString();
    const searchSlug = lang === "lv" ? "meklesana" : "search";
    return <Navigate to={`/${lang}/${searchSlug}${qs ? `?${qs}` : ""}`} replace />;
  }

  // /product/:slug
  if (first === "product" && segments[1]) {
    return <Navigate to={productPath(lang, segments[1])} replace />;
  }

  // /brand-rackets/:brand
  if (first === "brand-rackets" && segments[1]) {
    return <Navigate to={catalogPath(lang, "rackets", segments[1])} replace />;
  }

  // /<brand>-raketes
  if (BRAND_LEGACY_PATHS[first]) {
    return <Navigate to={catalogPath(lang, "rackets", BRAND_LEGACY_PATHS[first])} replace />;
  }

  // /padel-bumbas (legacy LV balls page)
  if (first === "padel-bumbas") {
    return <Navigate to={catalogPath(lang, "balls")} replace />;
  }

  // Known static pages
  if (LEGACY_STATIC_MAP[first]) {
    const key = LEGACY_STATIC_MAP[first];
    const rest = segments.slice(1).join("/");
    return <Navigate to={localizedPath(lang, key, rest || undefined)} replace />;
  }

  // /jaunumi/:slug → /{lang}/{newsSlug}/:slug
  if (first === "jaunumi") {
    return <Navigate to={localizedPath(lang, "news", segments.slice(1).join("/"))} replace />;
  }

  // /order/success
  if (first === "order" && segments[1] === "success") {
    return <Navigate to={localizedPath(lang, "orderSuccess")} replace />;
  }

  // Bare /:slug = legacy canonical product page
  if (segments.length === 1 && !RESERVED_LEGACY_ROOTS.has(first)) {
    return <Navigate to={productPath(lang, segments[0]) + search} replace />;
  }

  // Anything else - send to home; React Router will then render the NotFound under :lang.
  return <Navigate to={`/${lang}`} replace />;
}

/**
 * Validates the :lang segment and syncs LanguageContext to it.
 * Renders nothing — meant for use with a wrapped Outlet.
 */
export function LangSync({ children }: { children: React.ReactNode }) {
  const { lang: paramLang } = useParams<{ lang: string }>();
  const { lang, setLang } = useLanguage();
  const { pathname, search } = useLocation();

  const normalized = paramLang?.toLowerCase();
  const isValid = normalized === "lv" || normalized === "en";

  useEffect(() => {
    if (isValid && normalized && normalized !== lang) {
      setLang(normalized as Lang);
    }
  }, [isValid, normalized, lang, setLang]);

  if (!isValid) {
    // Unknown :lang — fall back to default language and keep the rest of the path.
    const rest = pathname.replace(/^\/[^/]+/, "");
    return <Navigate to={`/${DEFAULT_LANG}${rest}${search}`} replace />;
  }
  return <>{children}</>;
}

// Re-exports kept for callers that previously imported from this module.
export { LANGS, brandSlug, catalogPath, localizedPath, productPath, slugToType };
